<template>
  <header class="header" :class="{ 'header--scrolled': isScrolled, 'header--menu-open': isMobileMenuOpen, 'header--solid': !isHome }">
    <div class="header__container">
      <div class="header__main-row">
        <!-- Logo -->
        <router-link to="/" class="header__logo">
          <div class="logo-wrapper">
            <img :src="logoImg" alt="GELT - Global Alliance of Emerging Legal Talents" class="logo-image" />
          </div>
        </router-link>

        <div class="header__right">
          <!-- Desktop Navigation (rendered from the shared navItems list) -->
          <nav class="header__nav">
            <template v-for="item in navItems" :key="item.name">
              <div v-if="item.children" class="nav-item nav-dropdown" @mouseenter="openDropdown(item.name)" @mouseleave="closeDropdown">
                <router-link :to="item.path" class="nav-link" :class="{ 'is-active': isPathActive(item.match) }">{{ item.name }}</router-link>
                <div class="dropdown-menu" :class="{ 'dropdown-menu--open': activeDropdown === item.name }">
                  <router-link v-for="c in item.children" :key="c.path" :to="c.path" class="dropdown-item">{{ c.name }}</router-link>
                </div>
              </div>
              <div v-else class="nav-item">
                <router-link :to="item.path" class="nav-link" :class="{ 'is-active': isPathActive(item.match) }">{{ item.name }}</router-link>
              </div>
            </template>
          </nav>

          <!-- Actions -->
          <div class="header__actions">
            <div v-if="isAuthenticated" class="user-menu" @mouseenter="openDropdown('user')" @mouseleave="closeDropdown">
              <div class="user-avatar">
                <img v-if="userPhoto" :src="userPhoto" class="user-avatar__img" alt="" />
                <span v-else>{{ userInitials }}</span>
              </div>
              <div class="dropdown-menu dropdown-menu--right" :class="{ 'dropdown-menu--open': activeDropdown === 'user' }">
                <router-link v-if="isAdmin" to="/admin" class="dropdown-item">Admin Panel</router-link>
                <router-link to="/dashboard" class="dropdown-item">Dashboard</router-link>
                <a href="#" @click.prevent="handleLogout" class="dropdown-item dropdown-item--danger">Logout</a>
              </div>
            </div>
            <button v-else @click="openLogin" class="btn-login">MEMBER LOGIN</button>
          </div>
        </div>
      </div>

      <!-- Static Bottom Bar Removed in favor of dynamic link underlines -->

      <!-- Mobile Menu Toggle -->
      <button class="header__menu-toggle" @click="toggleMobileMenu" aria-label="Toggle menu">
        <span class="menu-bar"></span>
        <span class="menu-bar"></span>
        <span class="menu-bar"></span>
      </button>
    </div>

    <!-- Mobile Menu -->
    <div class="mobile-menu" :class="{ 'mobile-menu--open': isMobileMenuOpen }">
      <div class="mobile-nav">
        <!-- Mobile Navigation (rendered from the same navItems list as desktop) -->
        <template v-for="item in navItems" :key="item.name">
          <div v-if="item.children" class="mobile-nav-section">
            <div class="mobile-nav-header">
              <router-link :to="item.path" class="mobile-nav-header__link" @click="closeMobileMenu">{{ item.name }}</router-link>
              <span class="mobile-arrow" :class="{ 'mobile-arrow--open': mobileOpenSection === item.name }" @click="toggleMobileSection(item.name)">▼</span>
            </div>
            <div class="mobile-nav-items" :class="{ 'mobile-nav-items--open': mobileOpenSection === item.name }">
              <router-link v-for="c in item.children" :key="c.path" :to="c.path" @click="closeMobileMenu">{{ c.name }}</router-link>
            </div>
          </div>
          <router-link v-else :to="item.path" class="mobile-nav-link" @click="closeMobileMenu">{{ item.name }}</router-link>
        </template>

        <div v-if="isAuthenticated" class="mobile-nav-section">
           <div class="mobile-nav-header" @click="toggleMobileSection('user')">
            My Account
            <span class="mobile-arrow" :class="{ 'mobile-arrow--open': mobileOpenSection === 'user' }">▼</span>
          </div>
          <div class="mobile-nav-items" :class="{ 'mobile-nav-items--open': mobileOpenSection === 'user' }">
            <router-link v-if="isAdmin" to="/admin" @click="closeMobileMenu">Admin Panel</router-link>
            <router-link to="/dashboard" @click="closeMobileMenu">Dashboard</router-link>
            <a href="#" @click.prevent="handleLogout" class="dropdown-item">Logout</a>
          </div>
        </div>
        <button v-else @click="openLogin" class="mobile-nav-link mobile-nav-link--cta">Member Login</button>
      </div>
    </div>
  </header>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import logoImg from '@/assets/logo-gelt.png'

export default {
  name: 'AppHeader',
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    // Home has a full-bleed hero, so its header stays translucent at the top;
    // every other page uses a solid header so content doesn't show through.
    const isHome = computed(() => route.path === '/')

    // Single source of truth for the main nav — desktop bar AND mobile menu both
    // render from this, so they can never drift out of sync. Add/edit menu items here.
    const navItems = [
      { name: 'About', path: '/about', match: '/about', children: [
        { name: 'Intro to GELT', path: '/about' },
        { name: 'Why GELT?', path: '/about/why-law' }
      ] },
      { name: 'SpotLight', path: '/member-directory', match: '/member-directory' },
      { name: 'Events', path: '/news', match: '/news' },
      { name: 'Contact', path: '/contact', match: '/contact' },
      { name: 'Join GELT', path: '/join-law', match: '/join-law', children: [
        { name: 'Membership Criteria', path: '/join-law/membership-criteria' },
        { name: 'Membership Benefits', path: '/join-law/membership-benefits' }
      ] }
    ]

    const isScrolled = ref(false)
    const isMobileMenuOpen = ref(false)
    const activeDropdown = ref(null)
    const mobileOpenSection = ref(null)
    
    let dropdownTimeout = null
    
    const isAuthenticated = computed(() => store.getters.isAuthenticated)
    const userInitials = computed(() => store.getters.userInitials)
    const isAdmin = computed(() => store.state.user?.role === 'admin')
    const userPhoto = computed(() => store.state.user?.photo_path || '')
    
    const handleScroll = () => {
      isScrolled.value = window.scrollY > 0
    }
    
    const openDropdown = (name) => {
      if (dropdownTimeout) clearTimeout(dropdownTimeout)
      activeDropdown.value = name
    }
    
    const closeDropdown = () => {
      dropdownTimeout = setTimeout(() => {
        activeDropdown.value = null
      }, 150)
    }
    
    const toggleMobileMenu = () => {
      isMobileMenuOpen.value = !isMobileMenuOpen.value
      document.body.style.overflow = isMobileMenuOpen.value ? 'hidden' : ''
    }
    
    const closeMobileMenu = () => {
      isMobileMenuOpen.value = false
      document.body.style.overflow = ''
    }
    
    const toggleMobileSection = (section) => {
      mobileOpenSection.value = mobileOpenSection.value === section ? null : section
    }
    
    const handleLogout = () => {
      store.dispatch('logout')
      closeMobileMenu()
      router.push('/login')
    }

    const openLogin = () => {
      store.commit('TOGGLE_LOGIN_MODAL', true)
      closeMobileMenu()
    }
    
    const isPathActive = (path) => {
      // Handles basic prefix matching for sub-pages
      if (path === '/') return router.currentRoute.value.path === '/'
      
      const currentPath = router.currentRoute.value.path
      
      // Explicit section matching
      if (path === '/about' && currentPath.startsWith('/about')) return true
      if (path === '/join-law' && currentPath.startsWith('/join-law')) return true
      if (path === '/news' && (currentPath.startsWith('/news') || currentPath.startsWith('/meetings'))) return true
      if (path === '/member-directory' && (currentPath.startsWith('/member-directory') || currentPath.startsWith('/firms') || currentPath.startsWith('/lawyer'))) return true
      
      return currentPath.startsWith(path)
    }
    
    onMounted(() => {
      window.addEventListener('scroll', handleScroll)
      handleScroll()
    })
    
    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
    })
    
    return {
      isScrolled, isMobileMenuOpen, activeDropdown, mobileOpenSection,
      openDropdown, closeDropdown, toggleMobileMenu, closeMobileMenu, toggleMobileSection,
      isAuthenticated, userInitials, isAdmin, isHome, userPhoto, navItems, handleLogout, openLogin,
      logoImg, isPathActive
    }
  }
}
</script>

<style scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(7, 42, 91, 0.4);
  /* backdrop-filter: blur(20px); */
  border-bottom: 1px solid rgba(201, 169, 98, 0.15);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100px;
}


.header--scrolled {
  background: #072A5B;
  height: 80px;
}

/* Non-home pages: solid header (no translucency) so light page content doesn't bleed through */
.header--solid {
  background: #072A5B;
}

.header__container {
  /* Match the page .container so the logo (left) and avatar (right) line up
     with the content below — same max-width and horizontal padding. */
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 var(--space-xl);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.header__main-row {
  display: flex;
  align-items: center;
  justify-content: space-between; /* logo at left edge, actions at right edge */
  padding-bottom: 15px;
  gap: 2rem;
}

/* Logo */
.header__logo {
  display: inline-block;
  text-decoration: none;
  width: 286px;
  height: 50px;
  position: relative;
  z-index: 10;
  cursor: pointer;
  margin-left: 0;
  flex-shrink: 0;
  transform: translateY(-5px);
}

.logo-wrapper {
  width: 286px;
  height: 50px;
  display: flex;
  align-items: center;
}

.logo-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}



/* Navigation - Bordered Items */
.header__right {
  display: flex;
  align-items: center;
  gap: 3.5rem; /* Consistently match the main row gap */
  flex-grow: 1; /* Allow it to help position siblings if needed, though they are in one row now */
}

.header__nav {
  display: flex;
  gap: 3.5rem; /* Consistent uniform gap */
  align-items: center;
  flex: 1;
  justify-content: center; /* menu sits in the middle between logo and avatar */
}

.nav-item {
  position: relative;
}

.nav-link {
  display: block;
  color: rgba(255, 255, 255, 1);
  text-decoration: none;
  font-size: 16px;
  font-family: PingFangSC-Medium, "Microsoft YaHei", sans-serif;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.3s ease;
  padding-bottom: 25px; /* Create space for the indicator */
  position: relative;
}

.nav-link:hover {
  color: rgba(255, 255, 255, 1); /* Keep text white on hover */
}

/* Dynamic Indicator Bar */
.nav-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background-color: rgba(220, 193, 159, 1);
  transform: scaleX(0);
  transition: transform 0.3s ease;
  transform-origin: center;
}

/* Show indicator for active route or hover */
.nav-link.router-link-active::after,
.nav-link.is-active::after,
.nav-link:hover::after {
  transform: scaleX(1);
}
/* Removed .nav-link.router-link-active color change */

/* Dropdown */
.dropdown-menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  min-width: 240px;
  background: rgba(10, 22, 40, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(201, 169, 98, 0.2);
  border-radius: 8px;
  padding: 0.5rem 0;
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all 0.3s ease;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
}

.dropdown-menu--right {
  left: auto;
  right: 0;
}

.dropdown-menu--open {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-item {
  display: block;
  padding: 0.875rem 1.5rem;
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  margin: 0 0.5rem;
  border-radius: 4px;
}

.dropdown-item:hover {
  background: rgba(201, 169, 98, 0.1);
  color: var(--accent);
  border-left-color: var(--accent);
}

.dropdown-item--danger:hover {
  color: #ff4d4f;
  border-left-color: #ff4d4f;
  background: rgba(255, 77, 79, 0.1);
}

/* Actions */
.header__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.btn-login {
  width: 167px;
  height: 40px;
  background-color: #5D9CF5;
  border-radius: 4px;
  border: none;
  color: rgba(255, 255, 255, 1);
  font-size: 14px;
  font-family: PingFangSC-Regular, sans-serif;
  font-weight: normal;
  text-align: center;
  white-space: nowrap;
  line-height: 40px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  padding: 0;
  transform: translateY(-5px);
}

.btn-login:hover {
  background: #4A8BE4;
  box-shadow: 0 0 15px rgba(93, 156, 245, 0.4);
}

/* Bottom Bar removed */

/* User Avatar */
.user-menu {
  position: relative;
  cursor: pointer;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
  color: var(--primary-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  border: 2px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.user-menu:hover .user-avatar {
  transform: scale(1.05);
  box-shadow: 0 0 15px rgba(201, 169, 98, 0.4);
}

.user-avatar__img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  background: #fff; /* neutral backing so transparent PNGs don't pick up the gold gradient */
}

/* Mobile Menu Toggle */
.header__menu-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: 1px solid rgba(201, 169, 98, 0.3);
  padding: 0.75rem;
  cursor: pointer;
  border-radius: 4px;
}

.menu-bar {
  width: 20px;
  height: 2px;
  background: var(--accent);
  transition: all 0.3s ease;
}

.header--menu-open .menu-bar:nth-child(1) {
  transform: rotate(45deg) translate(5px, 5px);
}

.header--menu-open .menu-bar:nth-child(2) {
  opacity: 0;
}

.header--menu-open .menu-bar:nth-child(3) {
  transform: rotate(-45deg) translate(5px, -5px);
}

/* Mobile Menu */
.mobile-menu {
  display: none;
  position: fixed;
  top: 90px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(5, 8, 16, 0.98);
  backdrop-filter: blur(20px);
  overflow-y: auto;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
}

.mobile-menu--open {
  opacity: 1;
  visibility: visible;
}

.mobile-nav {
  padding: 2rem;
}

.mobile-nav-section {
  border-bottom: 1px solid rgba(201, 169, 98, 0.15);
}

.mobile-nav-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mobile-nav-header__link {
  flex: 1;
  padding: 1.25rem 0;
  color: var(--white);
  text-decoration: none;
  font-weight: 500;
  font-size: 1.1rem;
}

.mobile-nav-header__link:hover {
  color: var(--accent);
}

.mobile-arrow {
  font-size: 0.7rem;
  color: var(--accent);
  transition: transform 0.3s ease;
  /* Bigger tap target so the caret (expand) is easy to hit next to the link (navigate) */
  padding: 1.25rem 0.25rem 1.25rem 1.5rem;
  cursor: pointer;
}

.mobile-arrow--open {
  transform: rotate(180deg);
}

.mobile-nav-items {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.mobile-nav-items--open {
  max-height: 300px;
}

.mobile-nav-items a {
  display: block;
  padding: 0.875rem 0 0.875rem 1.5rem;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 0.95rem;
  border-left: 2px solid rgba(201, 169, 98, 0.3);
}

.mobile-nav-items a:hover {
  color: var(--accent);
  border-left-color: var(--accent);
}

.mobile-nav-link {
  display: block;
  padding: 1.25rem 0;
  color: var(--white);
  text-decoration: none;
  font-weight: 500;
  font-size: 1.1rem;
  border-bottom: 1px solid rgba(201, 169, 98, 0.15);
}

.mobile-nav-link--cta {
  color: var(--accent);
  margin-top: 1.5rem;
  border: 2px solid var(--accent);
  border-radius: 4px;
  text-align: center;
  padding: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.9rem;
}

@media (max-width: 1200px) {
  .logo-full {
    display: none;
  }
}

@media (max-width: 1024px) {
  .header__nav,
  .header__actions {
    display: none;
  }
  
  .header__menu-toggle,
  .mobile-menu {
    display: flex;
  }
  
  .mobile-menu {
    display: block;
  }
}
</style>
